import React from "react";
import { MdClose } from "react-icons/md";
import type { UserListItem } from "types/domain";
import { useLanguage } from "context/LanguageContext";
import type {
  OverlapInfo,
  ParticipantPickerType,
} from "../hooks/useSessionParticipantOverlap";
import ParticipantUserAvatar from "./ParticipantUserAvatar";
import SelectableParticipantCard from "./SelectableParticipantCard";

interface ParticipantPickerModalProps {
  pickerType: ParticipantPickerType | null;
  onClose: () => void;
  teacherSearch: string;
  trainerSearch: string;
  setTeacherSearch: (value: string) => void;
  setTrainerSearch: (value: string) => void;
  filteredManagerOptions: UserListItem[];
  filteredTrainerOptions: UserListItem[];
  sessionTeacherIds: string[];
  sessionTrainerIds: string[];
  setSessionTeacherIds: React.Dispatch<React.SetStateAction<string[]>>;
  setSessionTrainerIds: React.Dispatch<React.SetStateAction<string[]>>;
  managerOverlapMap: Map<string, OverlapInfo>;
  trainerOverlapMap: Map<string, OverlapInfo>;
}

const ParticipantPickerModal = ({
  pickerType,
  onClose,
  teacherSearch,
  trainerSearch,
  setTeacherSearch,
  setTrainerSearch,
  filteredManagerOptions,
  filteredTrainerOptions,
  sessionTeacherIds,
  sessionTrainerIds,
  setSessionTeacherIds,
  setSessionTrainerIds,
  managerOverlapMap,
  trainerOverlapMap,
}: ParticipantPickerModalProps): JSX.Element | null => {
  const { t } = useLanguage();
  if (!pickerType) return null;

  const options =
    pickerType === "teacher" ? filteredManagerOptions : filteredTrainerOptions;

  return (
    <div
      className="bg-black/40 fixed inset-0 z-[60] flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl rounded-2xl border border-line bg-surface p-5 shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between border-b border-line pb-3">
          <h5 className="text-base font-semibold text-ink">
            {pickerType === "teacher"
              ? t("sessionEditor.pickManagersTitle")
              : t("sessionEditor.pickTrainersTitle")}
          </h5>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1 text-ink-subtle hover:bg-surface-hover"
          >
            <MdClose className="h-4 w-4" />
          </button>
        </div>

        <input
          type="text"
          value={pickerType === "teacher" ? teacherSearch : trainerSearch}
          onChange={(event) => {
            if (pickerType === "teacher") {
              setTeacherSearch(event.target.value);
              return;
            }
            setTrainerSearch(event.target.value);
          }}
          placeholder={t("sessionEditor.searchByName")}
          className="mb-3 w-full rounded-lg border border-line bg-navy-50 px-3 py-2 text-sm text-ink outline-none placeholder:text-gray-400 focus:border-brand-500"
        />

        <div className="max-h-[420px] space-y-2 overflow-y-auto pr-1">
          {options.map((staff) => {
            const checked =
              pickerType === "teacher"
                ? sessionTeacherIds.includes(staff.id)
                : sessionTrainerIds.includes(staff.id);
            const alreadyInOtherRole =
              pickerType === "teacher"
                ? sessionTrainerIds.includes(staff.id)
                : sessionTeacherIds.includes(staff.id);
            const overlapInfo =
              pickerType === "teacher"
                ? managerOverlapMap.get(staff.id)
                : trainerOverlapMap.get(staff.id);
            const disabled =
              !checked &&
              (alreadyInOtherRole ||
                (pickerType !== "teacher" && Boolean(overlapInfo)));

            return (
              <SelectableParticipantCard
                key={staff.id}
                checked={checked}
                disabled={disabled}
                onCheckedChange={(isChecked) => {
                  if (pickerType === "teacher") {
                    setSessionTeacherIds((prev) => {
                      if (isChecked) {
                        if (prev.includes(staff.id)) return prev;
                        return [...prev, staff.id];
                      }
                      return prev.filter((idValue) => idValue !== staff.id);
                    });
                    return;
                  }

                  setSessionTrainerIds((prev) => {
                    if (isChecked) {
                      if (prev.includes(staff.id)) return prev;
                      return [...prev, staff.id];
                    }
                    return prev.filter((idValue) => idValue !== staff.id);
                  });
                }}
                leading={<ParticipantUserAvatar user={staff} />}
                content={
                  <>
                    <p className="truncate text-sm font-medium text-ink">
                      {staff.full_name}
                    </p>
                    <p className="truncate text-xs text-ink-muted">
                      {staff.email}
                    </p>
                    {alreadyInOtherRole && !checked ? (
                      <p className="mt-1 text-xs text-amber-700 dark:text-amber-300">
                        {t("sessionEditor.alreadyInOtherRole")}
                      </p>
                    ) : null}
                    {!alreadyInOtherRole && overlapInfo ? (
                      <p
                        className={`mt-1 text-xs ${
                          pickerType === "teacher"
                            ? "text-amber-700 dark:text-amber-300"
                            : "text-danger-600 dark:text-red-300"
                        }`}
                      >
                        {t("sessionEditor.overlapsWith", {
                          sessions: overlapInfo.sessionNames.join(", "),
                        })}
                      </p>
                    ) : null}
                  </>
                }
              />
            );
          })}
        </div>

        <div className="mt-4 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600"
          >
            {t("sessionEditor.done")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ParticipantPickerModal;
